import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControlLabel, Menu, MenuItem } from '@mui/material';
import { CheckboxComponent, DataGrid } from '@utriper/core-ui';

const CustomSelectResourceCell = ({ resourceOptions = [], resourcesSelected = [], handleSelectResource, translate }) => {
    const [anchorEl, setAnchorEl] = useState(false);
    const handleClick = (e) => setAnchorEl(e?.currentTarget);

    const handleClose = () => setAnchorEl(null);

    const isSelected = (categoryId, resourceId) => {
        const index = resourcesSelected?.findIndex((obj) => obj[categoryId]);
        if (index !== -1) {
            const resources = resourcesSelected?.[index][categoryId];
            return resources.includes(resourceId);
        }

        return false;
    };

    const getOptionLabel = () => {
        let label = translate('coreUi.data_grid.select');

        if (resourcesSelected?.length > 0) {
            label = resourceOptions
                .filter((option) => isSelected(option?.categoryId, option?.id))
                .map((option) => option?.label)
                .join(', ');
        }

        return label;
    };

    return (
        <Fragment>
            <Button onClick={handleClick} sx={{ width: '100%', height: '100%' }}>
                {getOptionLabel()}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {resourceOptions.map((resourceOption) => (
                    <MenuItem key={resourceOption.id}>
                        <FormControlLabel sx={{ m: 0 }} control={<CheckboxComponent checked={isSelected(resourceOption?.categoryId, resourceOption?.id)} onChange={(checked) => handleSelectResource(resourceOption?.categoryId, resourceOption.id, checked)} />} label={resourceOption.label} />
                    </MenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};

const ResourceGrid = ({ categories, resourcesSelected, handleResource }) => {
    const { t: translate } = useTranslation();
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([
        { columnId: 'categories', width: 150 },
        { columnId: 'resources', width: 150 },
    ]);
    const headerRow = useMemo(
        () => [
            {
                rowId: 'header',
                cells: [
                    {
                        type: 'header',
                        text: translate('coreUi.data_grid.category'),
                        style: {
                            fontSize: '13px',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                        },
                    },
                    {
                        type: 'header',
                        text: translate('coreUi.data_grid.resource'),
                        style: {
                            fontSize: '13px',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                        },
                    },
                ],
                height: 35,
            },
        ],
        [translate]
    );

    const handleSelectResource = (categoryId, resourceId, checked) => {
        const current = resourcesSelected || [];
        const index = current.findIndex((obj) => obj[categoryId]);

        let newResourcesSelected = [...current];
        if (checked) {
            if (index === -1) {
                newResourcesSelected.push({
                    [categoryId]: [resourceId],
                });
            } else {
                const resources = newResourcesSelected[index][categoryId];

                if (!resources.includes(resourceId)) {
                    newResourcesSelected[index] = {
                        [categoryId]: [...resources, resourceId],
                    };
                }
            }
        } else {
            if (index !== -1) {
                const resources = newResourcesSelected[index][categoryId];

                const filtered = resources.filter((id) => id !== resourceId);

                if (filtered.length === 0) {
                    newResourcesSelected.splice(index, 1);
                } else {
                    newResourcesSelected[index] = {
                        [categoryId]: filtered,
                    };
                }
            }
        }

        handleResource(newResourcesSelected);
    };

    const getRows = useCallback(
        (values) =>
            values?.map((value, idx) => {
                const resourceOptions = value?.resources?.map((resource) => ({
                    id: resource?.id,
                    label: resource?.name,
                    categoryId: value?.resourceCategoryId,
                }));

                return {
                    rowId: idx++,
                    cells: [
                        { type: 'text', text: value?.name || '' },
                        {
                            type: 'customCell',
                            text: '',
                            value: '',
                            children: <CustomSelectResourceCell resourceOptions={resourceOptions} resourcesSelected={resourcesSelected} handleSelectResource={handleSelectResource} translate={translate} />,
                        },
                    ],
                    height: 35,
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resourcesSelected]
    );

    useEffect(() => {
        if (categories?.length > 0) {
            setRows([...headerRow, ...getRows(categories)]);
        } else {
            setRows(headerRow);
        }
    }, [categories, headerRow, getRows]);

    useLayoutEffect(() => {
        const updateColumnWidths = () => {
            const container = document.getElementById('resource-grid-container');
            if (container) {
                const containerWidth = container.clientWidth;
                const newWidth = Math.max(containerWidth / columns.length, 150);
                setColumns(columns.map((col) => ({ ...col, width: newWidth })));
            }
        };

        updateColumnWidths();
        window.addEventListener('resize', updateColumnWidths);
        return () => {
            window.removeEventListener('resize', updateColumnWidths);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns.length]);

    return (
        <Box id="resource-grid-container">
            <DataGrid rows={rows} columns={columns} />
        </Box>
    );
};

export default ResourceGrid;
